/**
 * Henvendelser (the menu's «Forespørsler» until PM gaps B10, 2026-11-01): the request inbox.  CA-27, CA-38, CA-50.
 *
 * Every control is an endpoint that exists, or it is not on the screen; what
 * the API cannot do is said in words on the card. Carried over:
 *  - the queue, «Satt opp» (by date, so a nikah can be closed as Gjennomført
 *    after the day) and a search across every status;
 *  - confidential requests are hidden without saying what they are, and a
 *    404 on one reads the same as a request that does not exist, on purpose;
 *  - write actions need requests.write (fault 93) — without it the card is
 *    for reading, and says why, instead of buttons that answer 403;
 *  - a request somebody else has is let go first, never taken over silently;
 *  - rejecting asks for the reason first; closing asks what was done;
 *  - documents are downloaded with the session, never as a bare link.
 */
import { Forms, STATUS_WORDS } from '../core';
import React, { useEffect, useRef, useState } from 'react';
import { can, call, download, errText, getSession, post, send, type Body, type Sent } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty, useNav } from '../lib/nav';
import { isoToday, kb, longDate, osloTime } from '../lib/format';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Msg, Tag, type TagTone } from '../components/ui';

const KIND_NO: Record<string, string> = { nikah: 'Nikah (vielse)', janazah: 'Janazah', tour: 'Omvisning', fatiha: 'Fatiha', counselling: 'Samtale med imam', other: 'Kontakt', contact: 'Kontakt' };
// The words are core's (requests.STATUS_NO); the colour is the panel's. [tone, word], as this view reads it.
const REQ_TONE: Record<string, TagTone> = { new: 'red', in_progress: 'gold', scheduled: 'green', closed: 'green', rejected: 'grey' };
const REQ_STATUS: Record<string, [TagTone, string]> = Object.fromEntries(Object.entries(STATUS_WORDS.request).map(([k, w]) => [k, [REQ_TONE[k] || 'grey', w]]));
const URGENCY_NO: Record<string, [TagTone, string]> = { critical: ['red', 'Langt over fristen'], overdue: ['red', 'Over fristen'], due_soon: ['gold', 'Frist snart'], ok: ['green', 'Innenfor fristen'] };
const REQ_FIELD_NO: Record<string, string> = { party_a_name: 'Navn, part 1', party_b_name: 'Navn, part 2', contact_name: 'Navn', contact_phone: 'Telefon', contact_email: 'E-post',
  preferred_date: 'Ønsket dato', alternative_date: 'Alternativ dato', witnesses: 'Antall vitner', note: 'Melding', organisation: 'Skole eller organisasjon', group_size: 'Antall personer',
  age_group: 'Aldersgruppe', deceased_name: 'Navn på avdøde', date_of_death: 'Dødsdato', relation: 'Relasjon til avdøde', preferred_contact: 'Ønsker å bli kontaktet på', subject: 'Emne' };
const REQ_VALUE_NO: Record<string, string> = { phone: 'telefon', sms: 'SMS', in_person: 'møte i moskeen', email: 'e-post' };
const CLOSE_OUTCOMES = ['Besvart', 'Gjennomført', 'Avtalt og bekreftet', 'Henvist videre', 'Avslått', 'Trukket av den som spurte', 'Annet'];
// A button names the status it moves to (core's word); «Avvis» is the act, not the status («Avvist»).
const STATUS_BTN: Record<string, string> = { in_progress: STATUS_WORDS.request.in_progress, scheduled: STATUS_WORDS.request.scheduled, rejected: 'Avvis' };
const DOC_NO: Record<string, string> = { provingsattest: 'Prøvingsattest fra Skatteetaten', id_party_a: 'Legitimasjon, part 1', id_party_b: 'Legitimasjon, part 2' };

/**
 * SI-04 (audit 2026-10-19): «Send e-post» for an address somebody typed into a public form. The address is encoded too
 * (its @ kept), so a stored `a@x.no?cc=…&body=…` from before the stricter check opens a mail to that one odd address —
 * never with a hidden copy or a body the requester wrote.
 */
export const mailtoHref = (email: string, ref: string | null | undefined) =>
  `mailto:${encodeURIComponent(email).replace(/%40/g, '@')}?subject=${encodeURIComponent('Henvendelse ' + (ref || ''))}`;

const reqKind = (k: string) => KIND_NO[k] || k;
const ageText = (u: Body) => !u ? '' : u.ageDays >= 1 ? `venter ${u.ageDays} d` : `venter ${u.ageHours} t`;
const statusLabel = (x: string) => (REQ_STATUS[x] || [0, x])[1].toLowerCase();
const tel = (p: string) => 'tel:' + String(p).replace(/\s/g, '');
type Staff = Map<string, { id: string; name: string; canReadConfidential: boolean }>;

function whoIs(id: string | null | undefined, staff: Staff): string | null {
  if (!id) return null;
  const me = getSession();
  if (me && id === me.user.id) return 'deg';
  return (staff.get(id) && staff.get(id)!.name) || 'en kollega';
}

/** A refused request action, in words a person can act on. */
export function requestRefusal(res: Sent, missing: Body[] = []): string {
  const b = res.body || {};
  if (res.status === 403) return 'Du har ikke tilgang til å behandle henvendelser (tilgangen «Behandle henvendelser» mangler). Be en administrator om den.';
  if (b.code === 'missing_documents') {
    const names = (b.missing || []).map((id: string) => (missing.find((m) => m.id === id) || {}).label_no || DOC_NO[id] || id);
    return `Kan ikke settes opp før alle dokumentene er mottatt (krav ved vigsel). Mangler: ${names.join(', ')}.`;
  }
  if (b.code === 'invalid_transition') return `Statusen kan ikke endres fra «${statusLabel(b.from)}» til «${statusLabel(b.to)}».`
    + ((b.allowed || []).length ? ` Mulig nå: ${b.allowed.map(statusLabel).join(', ')}.` : '');
  if (b.code === 'reason_required') return 'Skriv hvorfor henvendelsen avvises.';
  if (b.code === 'use_close') return 'Bruk «Lukk henvendelsen» — lukking krever en beskrivelse av hva som ble gjort.';
  if (b.code === 'fodselsnummer_in_value') return 'Notatet ser ut til å inneholde et fødselsnummer. Ta det bort — fødselsnummer lagres aldri i plattformen.';
  const REASON: Record<string, string> = { assigned_to_someone_else: 'En kollega har allerede saken.', already_closed: 'Henvendelsen er allerede lukket.',
    invalid_transition: 'Henvendelsen kan ikke lukkes fra den statusen den har.', resolution_required: 'Skriv kort hva som ble gjort.' };
  return b.message_no || REASON[b.reason] || errText(b);
}

export function Inbox() {
  const { refreshBadges } = useNav();
  const { data, error, reload } = useLoad<Body>('/requests');
  const [staff, setStaff] = useState<Staff>(new Map());
  const [open, setOpen] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState<{ q: string; results: Body[] | null; note: string }>({ q: '', results: null, note: '' });
  const [flash, setFlash] = useState<[string, boolean] | null>(null);
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    // Everyone who can read the inbox, with who may read confidential ones (2026-09-11).
    call('/requests/assignees').then((d) => setStaff(new Map((d.items || []).map((u: Body) => [u.id, u])))).catch(() => { /* names fall back to "en kollega" */ });
  }, []);

  async function runSearch(v: string) {
    if (!v) return setSearch({ q: '', results: null, note: '' });
    if (v.length < 2) return setSearch({ q: v, results: null, note: 'Skriv minst to tegn.' });
    const r = await send('/requests?q=' + encodeURIComponent(v));
    if (!r.ok) return setSearch({ q: v, results: null, note: errText(r.body) });
    const res: Body[] = r.body.results || [];
    setSearch({ q: v, results: res, note: res.length ? `${res.length === 50 ? 'De 50 nyeste av mange' : res.length} treff for «${v}»${res.length === 50 ? ' — søk mer presist' : ''}.`
      : `Ingen treff for «${v}». Fortrolige henvendelser du ikke har tilgang til, vises ikke.` });
  }

  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const queue: Body[] = data.queue || [], sum = data.summary || {}, sched: Body[] | null = Array.isArray(data.scheduled) ? data.scheduled : null;
  const hid = data.hiddenConfidential || 0;
  const openBtn = (r: Body) => <Btn ghost aria-controls="ib-detail" aria-label={`Åpne ${r.ref_no || ''}`} onClick={() => { setFlash(null); setOpen(r.id); setSeq(seq + 1); }}>Åpne</Btn>;
  const rowProps = (r: Body) => ({ key: r.id, className: r.id === open ? 'sel' : undefined, 'aria-current': r.id === open ? ('true' as const) : undefined });
  const kindCell = (r: Body) => <>{reqKind(r.kind)}{r.confidential && <> <Tag tone="grey">fortrolig</Tag></>}</>;
  const today = isoToday();
  // After an action the lists are fetched again; the open case is NOT rebuilt (QA C, 2026-10-16): it reloads itself and
  // keeps what was half written in its other boxes — a remount wiped a note somebody was typing.
  const after = async () => { await reload(); refreshBadges(); if (search.q) await runSearch(search.q); };

  return (
    <>
      <Card title={`Innboks · ${sum.open ?? queue.length} åpne` + (sum.overdue ? ` · ${sum.overdue} over fristen` : '') + (sum.unassigned ? ` · ${sum.unassigned} ikke tildelt` : '')}
            hint="Erstatter e-posten som skjemaene på nettsiden sender i dag. Alt på ett sted, med status og referansenummer — enten forespørselen kom fra appen eller fra nettsiden. Den som har ventet lengst i forhold til fristen for sin type står øverst.">
        <table className="t">
          <thead><tr><th scope="col">Ref.</th><th scope="col">Tjeneste</th><th scope="col">Fra</th><th scope="col">Ønsket dato</th><th scope="col">Status</th><th scope="col">Svarfrist</th><th scope="col">Handling</th></tr></thead>
          <tbody>{queue.length ? queue.map((r) => {
            const u = r.urgency || {}; const [ut, ul] = URGENCY_NO[u.level] || ['grey', u.level || ''];
            const [st, sl] = REQ_STATUS[r.status] || ['grey', r.status]; const who = whoIs(r.assigned_to, staff);
            return (
              <tr {...rowProps(r)}>
                <td>{r.ref_no || r.id.slice(0, 8)}</td>
                <td>{kindCell(r)}{r.fileCount ? <><br /><span className="note">{r.fileCount} vedlegg</span></> : null}</td>
                <td dir="auto">{r.requester_name || '—'}</td>
                <td>{r.requested_date ? longDate(r.requested_date) : '—'}</td>
                <td><Tag tone={st}>{sl}</Tag>{who && <><br /><span className="note">tildelt {who}</span></>}</td>
                <td><Tag tone={ut}>{ul}</Tag><br /><span className="note">{ageText(u)} · frist {u.targetHours} t</span></td>
                <td>{openBtn(r)}</td>
              </tr>);
          }) : <tr><td colSpan={7}>{sum.allClear_no || 'Ingen ubehandlede henvendelser.'}</td></tr>}</tbody>
        </table>
        {/* Say that something is hidden without saying what. */}
        {hid > 0 && <p className="note" style={{ margin: '10px 18px 14px' }}>{hid} fortrolig{hid === 1 ? '' : 'e'} henvendelse{hid === 1 ? '' : 'r'} vises ikke for deg. De leses bare av dem som har tilgangen «Fortrolige henvendelser», eller den saken er tildelt.</p>}
      </Card>

      {sched && (
        <Card title={`Satt opp · ${sched.length}`} hint="Avtalt, etter dato. Når det er gjennomført — for eksempel etter vielsen — åpner du saken her og lukker den med utfallet «Gjennomført».">
          <table className="t">
            <thead><tr><th scope="col">Ref.</th><th scope="col">Tjeneste</th><th scope="col">Fra</th><th scope="col">Dato</th><th scope="col">Tildelt</th><th scope="col">Handling</th></tr></thead>
            <tbody>{sched.length ? sched.map((r) => (
              <tr {...rowProps(r)}>
                <td>{r.ref_no || r.id.slice(0, 8)}</td><td>{kindCell(r)}</td><td dir="auto">{r.requester_name || '—'}</td>
                <td>{r.requested_date ? longDate(r.requested_date) : 'ingen dato'}{r.requested_date && r.requested_date < today && <><br /><Tag tone="gold">Datoen er passert — lukk saken</Tag></>}</td>
                <td>{whoIs(r.assigned_to, staff) || '—'}</td><td>{openBtn(r)}</td>
              </tr>)) : <tr><td colSpan={6}>Ingen saker er satt opp.</td></tr>}</tbody>
          </table>
        </Card>
      )}

      <Card title="Finn en henvendelse" hint="Søker i alle henvendelser — også lukkede og avviste. Fortrolige vises bare for dem som har tilgang til dem.">
        <CardBody>
          <form role="search" className="ib-acts" style={{ marginTop: 0 }} aria-label="Søk i henvendelser"
                onSubmit={(e) => { e.preventDefault(); void runSearch(q.trim()); }}>
            <Field label="Søk på referanse, navn eller telefon" style={{ margin: 0, flex: 1, minWidth: 240 }}>{(id) =>
              <input id={id} type="search" autoComplete="off" placeholder="F.eks. 2026-0184, Raza eller 412 90 118" value={q} onChange={(e) => setQ(e.target.value)} />}</Field>
            <button className="btn sm" type="submit">Søk</button>
            {search.q && <Btn ghost onClick={() => { setQ(''); void runSearch(''); }}>Tøm søket</Btn>}
          </form>
          <p className="note" role="status" style={{ margin: '10px 0 0' }}>{search.note}</p>
        </CardBody>
        {search.results && search.results.length > 0 && (
          <table className="t">
            <thead><tr><th scope="col">Ref.</th><th scope="col">Tjeneste</th><th scope="col">Fra</th><th scope="col">Ønsket dato</th><th scope="col">Status</th><th scope="col">Handling</th></tr></thead>
            <tbody>{search.results.map((x) => { const [st, sl] = REQ_STATUS[x.status] || ['grey', x.status]; const who = whoIs(x.assigned_to, staff);
              return (<tr {...rowProps(x)}><td>{x.ref_no || x.id.slice(0, 8)}</td><td>{kindCell(x)}</td><td dir="auto">{x.requester_name || '—'}</td>
                <td>{x.requested_date ? longDate(x.requested_date) : '—'}</td><td><Tag tone={st}>{sl}</Tag>{who && <><br /><span className="note">tildelt {who}</span></>}</td><td>{openBtn(x)}</td></tr>); })}</tbody>
          </table>
        )}
      </Card>

      {open && <Detail key={open + ':' + seq} id={open} staff={staff} flash={flash} onChanged={after} />}
    </>
  );
}

function Detail({ id, staff, flash, onChanged }: { id: string; staff: Staff; flash: [string, boolean] | null; onChanged: () => Promise<void> }) {
  const [res, setRes] = useState<Sent | null>(null);
  const [msg, say] = useMsg();
  const [assignTo, setAssignTo] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState(''); const [reasonBad, setReasonBad] = useState(false);
  const [note, setNote] = useState(''); const [noteBad, setNoteBad] = useState(false);
  const [outcome, setOutcome] = useState(CLOSE_OUTCOMES[0]); const [resolution, setResolution] = useState('');
  // PM gaps B11: a note, a closing text or a rejection reason typed and not saved — leaving asks first.
  useDirty(`request-${id}`, Boolean(note.trim() || resolution.trim() || (rejecting && reason.trim())));
  const [busy, setBusy] = useState(false);
  const head = useRef<HTMLHeadingElement>(null);
  const card = useRef<HTMLDivElement>(null);

  const load = () => send('/requests/' + encodeURIComponent(id)).then((r) => { setRes(r); return r; });
  useEffect(() => {
    void load().then(() => {
      if (flash) say(flash[0], flash[1] ? 'ok' : 'err');
      // Focus to the heading on opening.
      setTimeout(() => { head.current?.focus({ preventScroll: true }); if (!flash) card.current?.scrollIntoView({ block: 'nearest' }); }, 0);
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!res) return <div className="card" id="ib-detail"><div className="card-body">Henter henvendelsen …</div></div>;
  if (!res.ok) {
    const why = res.status === 403 ? 'Du har ikke tilgang til henvendelser.'
      : res.status === 404 ? 'Henvendelsen finnes ikke, eller den er fortrolig og ikke tildelt deg. Fortrolige henvendelser (fatiha, samtale med imam) vises bare for dem som har tilgangen «Fortrolige henvendelser», eller den saken er tildelt.'
      : errText(res.body);
    return <div className="card" id="ib-detail"><h3>Henvendelsen kan ikke vises</h3><div className="card-body"><p className="msg err">{why}</p></div></div>;
  }

  const b = res.body; const q: Body = b.request || {}, u: Body = b.urgency || {}, f: Body = q.fields || {};
  const [st, sl] = REQ_STATUS[q.status] || ['grey', q.status]; const [ut, ul] = URGENCY_NO[u.level] || ['grey', u.level || ''];
  const isOpen = q.status !== 'closed' && q.status !== 'rejected';
  const me = getSession(); const mine = Boolean(me && q.assigned_to === me.user.id);
  const who = whoIs(q.assigned_to, staff);
  const files: Body[] = Array.isArray(b.files) ? b.files : [];
  const missing: Body[] = Array.isArray(b.missingDocuments) ? b.missingDocuments : [];
  const canWrite = can('requests.write');
  const allowed: string[] = (Array.isArray(b.allowedStatuses) ? b.allowedStatuses : []).filter((x: string) => STATUS_BTN[x]);
  const others = [...staff.values()].filter((x) => x.id !== q.assigned_to && (!q.confidential || x.canReadConfidential));
  const needDocs = q.kind === 'nikah' && missing.length > 0;
  const notes: Body[] = Array.isArray(b.notes) ? b.notes : [];

  const rows: [string, React.ReactNode][] = [];
  const kv = (k: string, v: React.ReactNode) => { if (v !== null && v !== undefined && v !== '') rows.push([k, v]); };
  kv(q.kind === 'nikah' ? 'Navn, part 1' : q.kind === 'tour' ? 'Kontaktperson' : 'Navn', q.requester_name || '—');
  if (q.phone) kv('Telefon', <a href={tel(q.phone)}>{q.phone}</a>);
  if (q.email) kv('E-post', <a href={mailtoHref(q.email, q.ref_no)}>{q.email}</a>);
  if (q.requested_date) kv('Ønsket dato', longDate(q.requested_date));
  if (q.requested_from || q.requested_to) kv('Tidsrom', [q.requested_from, q.requested_to].filter(Boolean).join('–'));
  for (const [k, v] of Object.entries(f)) {
    if (k === 'documents' || v === null || v === '' || typeof v === 'object') continue;
    // Fault 129: a tour's «Aldersgruppe» printed «youth». The form's own label
    // and choices are the source; the table above only overrides wording.
    const field = ((Forms.SCHEMAS[q.kind] || { fields: [] }).fields || []).find((x) => x.name === k);
    const choice = field && field.options ? field.options.find(([val]) => val === String(v)) : undefined;
    kv(REQ_FIELD_NO[k] || (field && field.label_no) || k.replace(/_/g, ' '),
       /date/.test(k) ? longDate(v) || String(v) : choice ? choice[1] : REQ_VALUE_NO[String(v)] || String(v));
  }
  kv('Kom fra', (q.source === 'website' ? 'nettsiden' : 'appen') + ' · ' + osloTime(q.created_at));
  kv('Tildelt', who || 'ingen ennå');

  async function act(what: string, to?: string) {
    const postTo = (path: string, body: Body) => post(`/requests/${encodeURIComponent(id)}/${path}`, body);
    let r: Sent | null = null, ok = '';
    setBusy(true);
    try {
      if (what === 'take') { r = await postTo('assign', { staffId: me!.user.id }); ok = 'Saken er din. Den står som «under behandling», og kolleger ser at du har den.'; }
      if (what === 'unassign') { r = await post('/requests/bulk', { action: 'unassign', ids: [id] }); ok = 'Tildelingen er fjernet. Saken står i køen for alle igjen.'; }
      if (what === 'assign') {
        if (!assignTo) return say('Velg hvem som skal ha saken.', 'err');
        const name = (staff.get(assignTo) || { name: 'kollegaen' }).name;
        // ONE call (QA review, 2026-10-16): a case somebody holds is handed over on purpose with `reassign` — it used to
        // be let go first and then assigned, and a refused second step left it with nobody.
        r = await postTo('assign', { staffId: assignTo, ...(q.assigned_to ? { reassign: true } : {}) });
        ok = (q.assigned_to ? 'Saken er gitt videre til ' : 'Saken er tildelt ') + name + '.';
      }
      if (what === 'status') { r = await postTo('status', { status: to });
        ok = ({ in_progress: 'Status: under behandling.', scheduled: 'Satt opp. Saken står nå under «Satt opp», etter dato — åpne den der og lukk den som «Gjennomført» etterpå.' } as Body)[to!] || 'Status endret.'; }
      if (what === 'reject') {
        if (!reason.trim()) { setReasonBad(true); return say('Skriv hvorfor henvendelsen avvises.', 'err'); }
        r = await postTo('status', { status: 'rejected', reason: reason.trim() }); ok = 'Avvist. Begrunnelsen er lagret som notat. Husk å gi beskjed til den som spurte.';
      }
      if (what === 'note') {
        if (!note.trim()) return say('Notatet er tomt.', 'err');
        r = await postTo('notes', { body: note.trim() }); if (!r.ok) setNoteBad(true); ok = 'Notatet er lagret.';
      }
      if (what === 'close') { r = await postTo('close', { resolution: resolution.trim() ? `${outcome} — ${resolution.trim()}` : '' }); ok = 'Lukket. Utfallet er lagret som notat, og henvendelsen er ute av køen.'; }
      if (!r) return;
      if (!r.ok) return say(requestRefusal(r, missing), 'err');
      // Only the box that was just used is emptied; anything typed elsewhere stays.
      if (what === 'note') setNote('');
      if (what === 'reject') { setReason(''); setRejecting(false); }
      if (what === 'close') setResolution('');
      if (what === 'assign') setAssignTo('');
      await load();
      say(ok, 'ok');
      setTimeout(() => head.current?.focus({ preventScroll: true }), 0);
      await onChanged();
    } finally { setBusy(false); }
  }

  return (
    <div className="card" id="ib-detail" ref={card}>
      <h3 ref={head} tabIndex={-1}>{q.ref_no || ''} · {reqKind(q.kind)} <Tag tone={st}>{sl}</Tag>{q.confidential && <> <Tag tone="grey">fortrolig</Tag></>}</h3>
      <p className="hint"><Tag tone={ut}>{ul}</Tag> {ageText(u)} · svarfrist {u.targetHours} timer for {reqKind(q.kind).toLowerCase()}{u.hoursLeft ? ` · ${u.hoursLeft} t igjen` : ''}</p>
      <CardBody>
        <Msg text={msg.text} tone={msg.tone} id="ib-msg" />
        <dl className="kv">{rows.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>
        <div className="ib-box"><h4>Vedlegg</h4>
          {files.length || missing.length ? <>
            {files.length > 0 && <><ul className="docs">{files.map((x) => (
              <li key={x.id}><span><b>{DOC_NO[x.docType] || x.docType || 'Vedlegg'}</b> · {x.filename} · {kb(x.bytes || 0)}{x.retentionUntil ? ` · slettes ${longDate(x.retentionUntil)}` : ''}</span>
                <Btn ghost aria-label={`Last ned ${x.filename}`} onClick={async () => {
                  const d = await download(x.downloadPath || `/v1/admin/requests/${encodeURIComponent(id)}/files/${encodeURIComponent(x.id)}`, x.filename || 'vedlegg');
                  if (d.ok) return say(`${x.filename || 'Vedlegget'} er lastet ned.`, 'ok');
                  say(d.status === 404 ? 'Vedlegget kan ikke vises: henvendelsen er fortrolig og ikke tildelt deg, eller vedlegget finnes ikke lenger.'
                    : d.status === 410 ? 'Vedlegget er slettet — oppbevaringstiden er ute. Be om et nytt om dere fortsatt trenger det.'
                    : d.status === 403 ? 'Du har ikke tilgang til henvendelser.' : d.status === 0 ? 'Vedlegget kunne ikke hentes — er nettet borte?' : (d.message || 'Vedlegget kunne ikke hentes.'), 'err');
                }}>Last ned</Btn></li>))}</ul>
              <p className="note" style={{ margin: '6px 0 0' }}>Kryptert lagring. Hver nedlasting logges (hvem og når). Slett filen fra din egen maskin når saken er ferdig.</p></>}
            {missing.length > 0 && <>
              <p className="docs-miss-h" style={{ margin: `${files.length ? '12px' : '0'} 0 4px` }}>Mangler {missing.length === 1 ? 'ett dokument' : missing.length + ' dokumenter'}:</p>
              <ul className="docs-missing">{missing.map((m) => <li key={m.id}>{m.label_no || DOC_NO[m.id] || m.id}</li>)}</ul>
              <p className="note" style={{ margin: '4px 0 0' }}>{q.kind === 'nikah' ? 'En nikah kan ikke bekreftes før alle dokumentene er kontrollert (krav ved vigsel). ' : ''}Be den som sendte henvendelsen om å sende det som mangler, og kontroller dokumentene før dere avtaler tid.</p></>}
          </> : <p className="note" style={{ margin: 0 }}>Ingen vedlegg. Denne typen henvendelse trenger ingen dokumenter.</p>}
        </div>
        <div className="ib-box"><h4>Behandle</h4>
          {!canWrite ? <>
            <p className="disabled-why">Du kan lese henvendelsene, men ikke behandle dem: tilgangen «Behandle henvendelser» mangler. Be en administrator om den.</p>
            <div className="ib-acts">{q.phone && <a className="btn sm ghost" href={tel(q.phone)}>Ring {q.phone}</a>}</div>
          </> : !isOpen ? <p>Henvendelsen er {sl.toLowerCase()}. Den er ute av køen.</p> : <>
            <div className="ib-acts">
              {!q.assigned_to && <Btn disabled={busy} onClick={() => act('take')}>Ta saken</Btn>}
              {q.assigned_to && <Btn ghost disabled={busy} onClick={() => act('unassign')}>{mine ? 'Gi fra meg saken' : 'Fjern tildelingen'}</Btn>}
              {q.phone && <a className="btn sm ghost" href={tel(q.phone)}>Ring {q.phone}</a>}
              {q.email && <a className="btn sm ghost" href={mailtoHref(q.email, q.ref_no)}>Send e-post</a>}
            </div>
            {others.length ? <>
              <div className="ib-acts" style={{ marginTop: 12 }}>
                <Field label="Tildel en kollega" style={{ margin: 0, minWidth: 240 }}>{(fid) =>
                  <select id={fid} value={assignTo} onChange={(e) => setAssignTo(e.target.value)}><option value="">Velg …</option>
                    {others.map((x) => <option key={x.id} value={x.id}>{x.name}{me && x.id === me.user.id ? ' (deg)' : ''}</option>)}</select>}</Field>
                <Btn ghost disabled={busy} onClick={() => act('assign')}>Tildel</Btn>
              </div>
              {q.confidential && <p className="note" style={{ margin: '6px 0 0' }}>Fortrolig: bare kolleger med tilgangen «Fortrolige henvendelser» kan velges.</p>}
            </> : <p className="note" style={{ margin: '10px 0 0' }}>Ingen andre kolleger kan få denne saken.</p>}
            {allowed.length > 0 && (
              <div className="ib-status"><h5 id="ib-status-h">Endre status</h5>
                <div className="ib-acts" role="group" aria-labelledby="ib-status-h">{allowed.map((x) => (
                  <Btn key={x} ghost disabled={busy} onClick={() => x === 'rejected' ? setRejecting(true) : act('status', x)}>{STATUS_BTN[x]}</Btn>))}</div>
                {allowed.includes('scheduled') && needDocs && <p className="note" style={{ margin: '6px 0 0' }}>«Satt opp» krever alle dokumentene (vigselsrett). Mangler nå: {missing.map((m) => m.label_no || DOC_NO[m.id] || m.id).join(', ')}.</p>}
                {rejecting && (
                  <div className="ib-reject">
                    <Field label="Hvorfor avvises henvendelsen?" required style={{ margin: '10px 0 6px' }} note="Lagres som internt notat. Den som spurte, får ikke se teksten — gi beskjed selv.">{(fid, d) =>
                      <textarea id={fid} rows={2} aria-describedby={d} aria-invalid={reasonBad || undefined} autoFocus value={reason} onChange={(e) => { setReason(e.target.value); setReasonBad(false); }} />}</Field>
                    <Btn danger disabled={busy} onClick={() => act('reject')}>Avvis henvendelsen</Btn>{' '}<Btn ghost onClick={() => setRejecting(false)}>Avbryt</Btn>
                  </div>)}
              </div>)}
          </>}
        </div>
        <div className="ib-box"><h4 id="ib-notes-h">Notater <span className="note">(interne — bare ansatte ser dem)</span></h4>
          {notes.length ? <ol className="ib-notes" aria-labelledby="ib-notes-h">{notes.slice().reverse().map((n, i) =>
            <li key={i}><div className="who">{n.author || 'Ukjent'} · {osloTime(n.createdAt)}</div><div className="txt">{n.body}</div></li>)}</ol>
            : <p className="note" style={{ margin: '0 0 8px' }}>Ingen notater ennå.</p>}
          {canWrite && <>
            <Field label="Legg til notat" style={{ margin: '10px 0 6px' }} note="Hva er gjort, avtalt eller ventet på. Aldri fødselsnummer. Maks 2000 tegn.">{(fid, d) =>
              <textarea id={fid} rows={2} maxLength={2000} aria-describedby={d} aria-invalid={noteBad || undefined} value={note} onChange={(e) => { setNote(e.target.value); setNoteBad(false); }} />}</Field>
            <Btn ghost disabled={busy} onClick={() => act('note')}>Lagre notat</Btn>
          </>}
        </div>
        {isOpen && canWrite && (
          <div className="ib-box"><h4>Lukk henvendelsen</h4>
            <p className="note" style={{ margin: '0 0 8px' }}>Skriv kort hva som ble gjort. Utfallet lagres som et internt notat, og den samme familien skal ikke måtte forklare seg på nytt neste gang.</p>
            <div className="row">
              <Field label="Utfall" className="field half" style={{ maxWidth: 260 }}>{(fid) =>
                <select id={fid} value={outcome} onChange={(e) => setOutcome(e.target.value)}>{CLOSE_OUTCOMES.map((o) => <option key={o}>{o}</option>)}</select>}</Field>
              <Field label="Hva ble gjort?" required className="field half">{(fid) => <textarea id={fid} rows={2} value={resolution} onChange={(e) => setResolution(e.target.value)} />}</Field>
            </div>
            <Btn disabled={busy} onClick={() => act('close')}>Lukk henvendelsen</Btn>
          </div>)}
      </CardBody>
    </div>
  );
}
